/** @prettier */
import classNames from 'classnames';
import { List, Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import Button from '@xanadu/ui-shared/components/widgets/Button';
import InputField from '@xanadu/ui-shared/components/widgets/InputField';
import translatedConstants from '@xanadu/ui-shared/constants';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import { useReduxBridge } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { useBalances, useImprovedTranslation, usePrevious } from '@xanadu/ui-shared/hooks';
import { dispatch as gtmDispatch } from '@xanadu/ui-shared/interfaces/googleTagManager';
import { userVirtualPageView as gtmUserVirtualPageView } from '@xanadu/ui-shared/interfaces/googleTagManager/actions';
import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { requestNetDepositLimit as netDepositLimitRequestAct } from '@xanadu/ui-shared/modules/depositLimits/actions';
import { displayModal as displayModalAct } from '@xanadu/ui-shared/modules/modal/actions';
import { depositRequest as depositRequestAct } from '@xanadu/ui-shared/modules/user/actions';
import { registrationRequest as registrationRequestAct } from '@xanadu/ui-shared/modules/userRegistration/actions';
import {
  logout as LogoutAct,
  loginRequest as loginRequestAct,
  loginUser as loginUserAct,
} from '@xanadu/ui-shared/modules/userSession/actions';

import { MOBILE_BREAKPOINT } from '../../../constants';

import { getClientString } from '../../../utils';

import Wallet from '../Wallet';

import SettingsDrop from './SettingsDrop';
import styles from './UserButtons.module.scss';

const UserButtons = ({
  loginRequest,
  loginUser,
  loginErrors,
  depositRequest,
  displayModal,
  balance,
  widthSize,
  freeBets,
  casinoPromotions,
  netDepositLimit,
  netDepositError,
  netDepositLimitRequest,
  userAffordabilityInfoProvided,
  userSegmentCategory,
  loadingNetDeposit,
  balanceLoading,
  registrationRequest,
  isLoggedIn,
  logout,
  edgeStatus,
  displaySettingsMenu,
  username,
  product,
}) => {
  const { clientName, clientSite } = useReduxBridge() || {};
  const {
    featuresFlags: { cashierEnabled = false } = {},
    customization: {
      Header: { exposureLabel = 'Exposure', balanceLabel = 'Available', walletVariant = 'v1' } = {},
    } = {},
  } = React.useContext(ChildrenPropsContext) || {};

  const { ERROR_MESSAGES_BY_CODES = {} } = translatedConstants;

  const [directLogging, setDirectLogging] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [depositButtonDisabled, setDepositButtonDisabled] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const [error, setError] = useState(loginErrors);

  const prevLoggedIn = usePrevious(isLoggedIn);

  const [depositButtonText, setDepositButtonText] = useState('Deposit');

  const {
    t,
    i18n: { language: activeLanguage },
  } = useImprovedTranslation();

  const isMobile = widthSize <= MOBILE_BREAKPOINT;

  const {
    bonusFreeFunds,
    bonusAmount,
    freeSpins,
    showBonusIcon,
    userAffordabilityInfoIsNotProvided,
    onDepositClick,
  } = useBalances({
    casinoPromotions,
    balance,
    balanceLoading,
    depositRequest,
    edgeStatus,
    freeBets,
    netDepositLimit,
    requestNetDepositLimit: netDepositLimitRequest, // DEPOSIT_REQUEST
    userAffordabilityInfoProvided,
    isMobile,
    product,
  });

  const hasEKYCErrorMessage = useMemo(
    () =>
      netDepositError.includes('EKYC check failed for the account:') || // TODO: errors, ERROR_CODES
      netDepositError.includes("Net deposit limits can't be set"), // TODO: errors, ERROR_CODES
    [netDepositError]
  );

  // Segment Category is one of "V", "X", "Y", "Z"
  const continueGBGFVCCheck = useMemo(
    () => userSegmentCategory !== 'V' && userAffordabilityInfoIsNotProvided,
    [userSegmentCategory, userAffordabilityInfoIsNotProvided]
  );

  const currentUrl = window.location.href;
  const onInputChange = ({ name, value }) => {
    switch (name) {
      case 'password':
        setPassword(value);
        break;
      case 'username':
        setEmail(value);
        break;
      case 'two-factor-code':
        setTwoFactorCode(value);
        break;
      default:
        break;
    }
  };

  const handleDirectLogin = () => {
    if (!email || !password) {
      setError(t('Missing credentials'));
      return;
    }

    setDirectLogging(true);

    setError('');

    const payload = {
      loginType: 'login',
      loginPayload: {
        username: email,
        password,
        twoFactorAuthCode: twoFactorCode,
      },
    };

    loginUser(payload);
  };

  const handleLogin = () => {
    loginRequest(true);
  };

  const handleJoin = () => {
    gtmDispatch(
      gtmUserVirtualPageView({
        currentUrl,
        eventName: 'Reg Process',
        virtualPageTitle: 'Step 1 - account',
        virtualPageURL: '/reg/step1/',
        previousURL: `${location.pathname}`,
        clickSource: { clickSource: `mb-join-now-button_${location.pathname}` },
      })
    );

    track('Registration', 'Registration opened', {
      description: 'header',
    });

    registrationRequest({ show: true, promoRegistrationImage: null, checked: false });
  };

  const handleCancelTwoFactor = () => {
    setEmail('');
    setPassword('');
    setTwoFactorCode('');
    setShowTwoFactor(false);
    setDirectLogging(false);
    loginRequest(false);
  };

  const loginKeyListener = (event) => {
    if (event.key === 'Enter') {
      handleDirectLogin();
    }
  };

  useEffect(() => {
    if (loginErrors && directLogging) {
      const errorMessage = typeof loginErrors === 'string' ? loginErrors : loginErrors.first();
      const field = errorMessage.get('field');

      const message =
        (errorMessage.get('messages') && errorMessage.get('messages').first()) ||
        errorMessage.get('message') ||
        '';
      const code =
        (errorMessage.get('codes') && errorMessage.get('codes').first()) ||
        errorMessage.get('code') ||
        '';

      const computedMessage = ERROR_MESSAGES_BY_CODES[code];

      if (field === 'mfa-code') {
        setError('');
        setShowTwoFactor(true);

        setError(computedMessage);

        return;
      }

      setError(computedMessage || message);
    }
  }, [loginErrors, directLogging]);

  useEffect(() => {
    setDirectLogging(false);
  }, [loginErrors]);

  useEffect(() => {
    if (cashierEnabled) {
      if (netDepositError) {
        if (hasEKYCErrorMessage) {
          displayModal({
            show: true,
            section: 'deposit',
          });
          setDepositButtonText('Upload');
        } else {
          setDepositButtonText('Try Again');
        }
      } else {
        setDepositButtonText('Deposit');
      }
    }
  }, [cashierEnabled, netDepositError, hasEKYCErrorMessage, activeLanguage]);

  useEffect(() => {
    let timer;

    const hasLoggedIn =
      typeof prevLoggedIn === 'boolean' && prevLoggedIn !== isLoggedIn && isLoggedIn;

    if (hasLoggedIn) {
      setDepositButtonDisabled(true);
      handleCancelTwoFactor(false);
      setError('');

      timer = setTimeout(() => {
        setDepositButtonDisabled(false);
      }, 2000);
    }

    return () => {
      if (!isLoggedIn) {
        clearTimeout(timer);
      }
    };
  }, [isLoggedIn, prevLoggedIn]);

  const directLoginButtonClass = classNames({
    [styles.button]: true,
    [styles.directLoginButton]: true,
  });

  const loginButtonClass = classNames({
    [styles.button]: true,
    [styles.loginButton]: true,
  });

  const walletClass = classNames({
    [styles.containerWallet]: true,
  });

  const clogButton = displaySettingsMenu && (
    <div className={styles.settings}>
      <SettingsDrop extraClasses={styles.settingsDrop} isLoggedIn={isLoggedIn} logoutFn={logout} />
    </div>
  );

  const clientString = getClientString(clientName, clientSite);

  const walletContent = (
    <React.Fragment>
      <Wallet
        balance={balance}
        balanceLoading={balanceLoading}
        username={username}
        bonusAmount={bonusAmount}
        bonusFreeFunds={bonusFreeFunds}
        freeSpins={freeSpins}
        freeBets={freeBets}
        showBonusIcon={showBonusIcon}
        isMobile={isMobile}
        exposureLabel={exposureLabel}
        balanceLabel={balanceLabel}
        walletVariant={walletVariant}
        cashierEnabled={cashierEnabled}
        continueGBGFVCCheck={continueGBGFVCCheck}
        loadingNetDeposit={loadingNetDeposit}
        netDepositError={netDepositError}
        hasEKYCErrorMessage={hasEKYCErrorMessage}
        depositButtonText={depositButtonText}
        depositButtonDisabled={depositButtonDisabled}
        onDepositClick={onDepositClick}
        clientString={clientString}
        t={t}
      />
      {clogButton}
    </React.Fragment>
  );

  return (
    <div className={styles.main}>
      {isLoggedIn ? (
        <div className={walletClass}>{walletContent}</div>
      ) : (
        <React.Fragment>
          <div className={styles.inputsContainer} onKeyDown={loginKeyListener} role="button">
            {showTwoFactor ? (
              <React.Fragment>
                <div className={styles.twoFactorContainer}>
                  <span>{t('Please enter your 2FA authenticator code')}</span>
                  <a
                    onClick={handleCancelTwoFactor}
                    role="button"
                    data-hook="cancel-2fa"
                    className={`.gtm_cta ${styles.link}`}
                  >
                    {t('Cancel')}
                  </a>
                </div>
                <InputField
                  name={'two-factor-code'}
                  dataHook={'2fa-top'}
                  type="password"
                  onPropertyChange={onInputChange}
                  placeholder={t('Code')}
                  value={twoFactorCode}
                />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Link to={'/reset-password'} data-hook="reset-password-v2" className=".gtm_cta">
                  {t('Trouble Logging In?')}
                </Link>
                <InputField
                  name={'username'}
                  dataHook={'username-top'}
                  type="text"
                  onPropertyChange={onInputChange}
                  placeholder={t('Username')}
                  value={email}
                />
                <InputField
                  name={'password'}
                  dataHook={'password-top'}
                  type="password"
                  onPropertyChange={onInputChange}
                  placeholder={t('Password')}
                  value={password}
                />
              </React.Fragment>
            )}
            {error ? <div className={styles.errorsContainer}>{error}</div> : null}
          </div>
          <div className={styles.buttonsContainer}>
            {showTwoFactor ? (
              <Button
                className={styles.button}
                disabled={!twoFactorCode}
                onClick={handleDirectLogin}
                sizeType="regular"
                buttonType="primary"
                dataHook="header-login-verify"
              >
                {t('Verify')}
              </Button>
            ) : (
              <React.Fragment>
                <Button
                  className={directLoginButtonClass}
                  onClick={handleDirectLogin}
                  sizeType="regular"
                  buttonType="secondary"
                  dataHook="header-direct-login"
                >
                  {t('Log In')}
                </Button>
                <Button
                  className={loginButtonClass}
                  onClick={handleLogin}
                  sizeType="regular"
                  buttonType="secondary"
                  dataHook="header-open-login-modal"
                >
                  {t('Log In')}
                </Button>
                <Button
                  inverted
                  className={styles.button}
                  onClick={handleJoin}
                  sizeType="regular"
                  buttonType="primary"
                  dataHook="header-join-now"
                >
                  {t('Join Now')}
                </Button>
              </React.Fragment>
            )}
            {clogButton}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

UserButtons.propTypes = {
  loginRequest: PropTypes.func.isRequired,
  loginUser: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  depositRequest: PropTypes.func.isRequired,
  displayModal: PropTypes.func.isRequired,
  balance: PropTypes.object.isRequired,
  widthSize: PropTypes.number.isRequired,
  edgeStatus: PropTypes.number.isRequired,
  freeBets: PropTypes.instanceOf(List),
  casinoPromotions: PropTypes.instanceOf(Map),
  netDepositLimit: PropTypes.number,
  loadingNetDeposit: PropTypes.bool.isRequired,
  netDepositError: PropTypes.string,
  userAffordabilityInfoProvided: PropTypes.bool,
  userSegmentCategory: PropTypes.string,
  netDepositLimitRequest: PropTypes.func.isRequired,
  balanceLoading: PropTypes.bool.isRequired,
  registrationRequest: PropTypes.func.isRequired,
  loginErrors: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(List), PropTypes.string]),
  logout: PropTypes.func.isRequired,
  displaySettingsMenu: PropTypes.bool,
  username: PropTypes.string,
  product: PropTypes.string,
};

const mapDispatchToProps = {
  loginRequest: loginRequestAct,
  loginUser: loginUserAct,
  displayModal: displayModalAct,
  netDepositLimitRequest: netDepositLimitRequestAct,
  depositRequest: depositRequestAct,
  registrationRequest: registrationRequestAct,
  logout: LogoutAct,
};

function mapStateToProps(state) {
  return {
    balance: state.getIn(['user', 'balance']),
    balanceLoading: state.getIn(['user', 'balanceLoading']),
    username: state.getIn(['user', 'username']),
    edgeStatus: state.getIn(['application', 'edgeStatus']),
    loginErrors: state.getIn(['user', 'loginErrors']),
    casinoPromotions: state.getIn(['promotions', 'main', 'casinoPromotions']),
    freeBets: state.getIn(['promotions', 'manualBonus', 'data']),
    widthSize: state.getIn(['application', 'widthSize']),
    userAffordabilityInfoProvided: state.getIn(['user', 'affordabilityInfoProvided']),
    userSegmentCategory: state.getIn(['user', 'segmentCategory']),
    loadingNetDeposit: state.getIn(['depositLimits', 'loadingNetDeposit']),
    netDepositError: state.getIn(['depositLimits', 'error']),
    netDepositLimit: state.getIn(['depositLimits', 'netMonthlyDeposit']),
    isLoggedIn: state.getIn(['user', 'isLoggedIn']),
    product: state.getIn(['application', 'product']),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UserButtons);
